import { Cached } from '@mui/icons-material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Box, Grid, IconButton, Tooltip, Stack } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Cell } from 'react-table';
import {
  errorFileForBulkUploads,
  getPresignedURLForGet,
  getUploadHistorySearch,
  IFileInfoDTO,
  IUploadHistoryRequest,
  IUploadHistoryResponse,
  refreshBatchCounts,
} from 'wisestep-api';
import {
  WsDataTable,
  WsTypography,
  WsTypoColors,
  WsTitleBanner,
  WsColors,
  Spinner,
} from 'wisestep-base';

interface HistoryResponse {
  historyData: IUploadHistoryResponse[];
  totalCount: number;
}

const UploadHistory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [query, setQuery] = useState<IUploadHistoryRequest>({
    size: 50,
    page: 0,
    sort: 'created_on,desc',
    workspaceId: '',
  });
  const [history, setHistory] = useState<HistoryResponse>({ historyData: [], totalCount: 0 });

  const refreshCounts = async (cell: Cell) => {
    const row = cell.row.original as IUploadHistoryResponse;
    if (row.batchCode) {
      const response = await refreshBatchCounts(row.batchCode);
      setHistory({
        ...history,
        historyData: updatedHistory(response.data),
      });
    }
  };

  const updatedHistory = (batch: IUploadHistoryResponse) =>
    history?.historyData?.map((item) => {
      if (item.batchCode == batch.batchCode) {
        return {
          ...item,
          totalSelected: batch.totalSelected,
          totalUploaded: batch.totalUploaded,
          totalProcessed: batch.totalProcessed,
          totalFailed: batch.totalFailed,
        };
      }
      return item;
    });

  const openPipeline = async (cell: Cell) => {
    const row = cell.row.original as IUploadHistoryResponse;
    if (row.batchCode) navigate(`/candidates/mappings?batchId=${row.batchCode ?? ''}`);
  };

  const columns = [
    {
      Header: '',
      accessor: 'isCompleted',
      Cell: (cell: Cell) =>
        cell.value ? (
          <Tooltip title="Upload Completed">
            <CheckBoxIcon sx={{ color: WsColors.Green200 }} />
          </Tooltip>
        ) : (
          <Stack direction="row">
            <Box pt={1}>
              <Tooltip title="Processing">
                <HistoryToggleOffIcon sx={{ color: WsColors.Yellow400 }} />
              </Tooltip>
            </Box>
            <Tooltip title="Refresh to Update">
              <IconButton onClick={() => refreshCounts(cell)}>
                <Cached />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
    },
    {
      Header: 'Batch Code',
      accessor: 'batchCode',
      id: '1',
    },
    {
      Header: 'Created On',
      accessor: 'createdOn',
      id: '2',
      Cell: (cell: Cell) => {
        const row = cell.row.original as IUploadHistoryResponse;
        const uploadTime = row.createdOn;
        if (uploadTime) {
          const formattedDate = new Date(uploadTime).toLocaleString();
          return <WsTypography color={WsTypoColors.SECONDARY}>{formattedDate}</WsTypography>;
        }
      },
    },
    {
      Header: 'Total CVs Selected',
      accessor: 'totalSelected',
      id: '3',
      Cell: (cell: Cell) => (
        <WsTypography color={WsTypoColors.SECONDARY}>{cell.value}</WsTypography>
      ),
    },
    {
      Header: 'CVs Uploaded',
      accessor: 'totalUploaded',
      id: '6',
      Cell: (cell: Cell) => (
        <WsTypography color={WsTypoColors.SECONDARY}>{cell.value}</WsTypography>
      ),
    },
    {
      Header: 'CVs Processed',
      accessor: 'totalProcessed',
      id: '5',
      Cell: (cell: Cell) => (
        <WsTypography color={WsTypoColors.SECONDARY}>{cell.value}</WsTypography>
      ),
    },

    {
      Header: 'CVs Not Processed',
      accessor: 'totalFailed',
      id: '4',
      Cell: (cell: Cell) => {
        const historyItem = cell.row.original as IUploadHistoryResponse;
        return (
          <WsTypography color={WsTypoColors.SECONDARY}>
            {cell.value}
            {cell.value > 0 &&
              historyItem.totalProcessed != undefined &&
              historyItem.totalFailed != undefined &&
              historyItem.totalUploaded ===
                historyItem.totalProcessed + historyItem.totalFailed && (
                <IconButton onClick={() => handleCloudIconClick(cell)}>
                  <CloudDownloadIcon color="primary" fontSize="small" />
                </IconButton>
              )}
          </WsTypography>
        );
      },
    },

    {
      Header: '',
      accessor: 'batchCode',
      id: '7',
      Cell: (cell: Cell) => (
        <Stack direction="row" alignItems="center" spacing={1}>
          <WsTypography
            color={WsTypoColors.PRIMARY600}
            onClick={() => {
              const row = cell.row.original as IUploadHistoryResponse;
              const importFrom = row.importFrom;

              if (importFrom === 'GoogleDrive') {
                navigate(`/import-candidates/bulk-upload-files-report/gdrive/${cell.value}`);
              } else {
                navigate(`/import-candidates/bulk-upload-files-report/${cell.value}`);
              }
            }}
            cursor="pointer"
          >
            <u>View Imported Files</u>
          </WsTypography>

          <Tooltip title={'View In Pipeline'}>
            <IconButton onClick={() => openPipeline(cell)}>
              <OpenInNewIcon color="primary" fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];
  const customData = useMemo(
    () =>
      history.historyData?.map((history) => ({
        ...history,
        isCompleted:
          history.totalSelected == (history?.totalProcessed ?? 0) + (history?.totalFailed ?? 0),
      })),
    [history]
  );

  const handleCloudIconClick = async (cell: Cell) => {
    try {
      const row = cell.row.original as IUploadHistoryResponse;
      const batchId = row.batchCode;
      let errorFileUrl = row.errorFileUrl;

      if (!errorFileUrl && row.batchCode) {
        const response = await errorFileForBulkUploads({ ...query, batchId });
        errorFileUrl = response.data.ErrorFileURL;
      }

      if (errorFileUrl) {
        try {
          const request: IFileInfoDTO = {
            fileName: errorFileUrl.split('?')[0] ?? '',
            fileType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            downloadAs: 'Bulk_Upload_Issues',
          };
          const response = await getPresignedURLForGet(request);
          if (response?.data) {
            window.open(response.data.preSignedUrl, '_blank');
          } else {
            console.error('Failed to fetch pre-signed URL.');
          }
        } catch (error) {
          console.error('Error while fetching pre-signed URL:', error);
        }
      } else {
        console.error('No errorFileURL available.');
      }
    } catch (error) {
      console.error('ErrorFilecreation ran into an issue', error);
    }
  };

  const filteredData =
    location.pathname === '/import-candidates/bulk-upload-report'
      ? customData.filter((item) => item.importFrom === 'LOCAL')
      : customData.filter((item) => item.importFrom === 'GoogleDrive');

  useEffect(() => {
    (async () => {
      const uploadHistory = await getUploadHistorySearch(query);
      setHistory({
        historyData: uploadHistory.data,
        totalCount: Number(
          uploadHistory.headers['x-total-count'] ?? uploadHistory.headers['X-Total-Count']
        ),
      });
    })();
  }, [query]);

  return (
    <Grid>
      <WsTitleBanner title="Bulk Uploads" goBack={() => navigate('/import-candidates')} />
      <Grid item xs={12} container justifyContent="flex-start" pt={2}>
        {filteredData.length > 0 ? (
          <WsDataTable
            columns={columns}
            data={filteredData}
            totalDataItems={history.totalCount}
            selectable={false}
            boldItems={['batchCode']}
            fetchPage={(page) => setQuery((query) => ({ ...query, page: page }))}
          />
        ) : (
          <Spinner />
        )}
      </Grid>
    </Grid>
  );
};

export default UploadHistory;
